import { memo, useMemo } from 'react';
import color from '@deprecated-ui/_style/color';
import Icon from '@ant-design/icons';
import moment from 'moment';

import Link from 'next/link';
import numeral from 'numeral';
import number from 'utils/number';
import { TableProps } from 'antd/lib/table';
import type { ColumnsType } from 'antd/es/table/interface';
import If from 'global/Common/If';
import { LightningFilledSvg } from 'features/day-trade/components/Icons';
import { clevertapDayTradeSortSelected } from 'lib/analytics/securities';
import {
  Td,
  Th,
  Table,
  DayTradeMultiplier,
  MultiplierBadge,
  HaircutBadge,
  HaircutCell,
  NewBadge,
} from './styled';
import {
  COLUMN_KEY,
  SPECIAL_BOARD_ALIAS,
  SPECIAL_SUBSECTOR_ALIAS,
} from './constants';
import Badge from './Badge';

interface TableCatalogProps {
  companyList: Array<any>;
  isLoading: boolean;
  liveData: any;
  showMultiplier?: boolean;
  isFRCatalog: boolean;
  subsectorAlias?: string;
  tableTitle?: string;
}

interface CustomColumnItem {
  grouping_by: { label: string };
}

const DEFAULT_KEY = {
  LAST: 'last',
  PERCENT: 'percent',
};

const LIVE_VALUE_KEY = {
  LAST: 'lastprice',
  PERCENT: 'percentage_change',
};

function TableCatalog({
  companyList,
  isLoading,
  liveData,
  showMultiplier = false,
  isFRCatalog = false,
  subsectorAlias = null,
  tableTitle = '',
}: TableCatalogProps) {
  const isTradingLimitSubsector =
    subsectorAlias?.toUpperCase() === SPECIAL_SUBSECTOR_ALIAS.TRADING_LIMIT;

  const hideDefaultSortOrder = useMemo(() => {
    const noSortOrder = [
      SPECIAL_SUBSECTOR_ALIAS.SUSPENDED,
      SPECIAL_SUBSECTOR_ALIAS.FCA,
    ];

    return noSortOrder.includes(subsectorAlias?.toUpperCase());
  }, [subsectorAlias]);

  const handleChange: TableProps<any>['onChange'] = () => {
    if (showMultiplier) {
      clevertapDayTradeSortSelected();
    }
  };

  const cellNumbersorter = ({
    prevValue,
    nextValue,
    defaultValueKey,
    liveValueKey,
  }) => {
    const liveLastPriceA = liveData[prevValue.symbol]?.[liveValueKey];
    const liveLastPriceB = liveData[nextValue.symbol]?.[liveValueKey];

    if (liveLastPriceA && liveLastPriceB) {
      return liveLastPriceA - liveLastPriceB;
    }

    try {
      const parsedA = parseFloat(prevValue[defaultValueKey]);
      const parsedB = parseFloat(nextValue[defaultValueKey]);
      return parsedA - parsedB;
    } catch (_err) {
      return 0;
    }
  };

  const getIgnoreSorting = (a: CustomColumnItem, b: CustomColumnItem) =>
    a.grouping_by?.label < b.grouping_by?.label ||
    a.grouping_by?.label > b.grouping_by?.label;

  const getEmptyRow = (custom_row: string) =>
    custom_row === 'row_today' || custom_row === 'row_common';

  const columnValueNonFR = [
    {
      title: <Th id="catalog-table-column-company">Company</Th>,
      key: COLUMN_KEY.COMPANY,
      dataIndex: 'name',
      shouldCellUpdate: () => false,
      sorter: (a, b) => {
        if (getIgnoreSorting(a, b)) {
          return 0;
        }

        if (getEmptyRow(a.custom_row) || getEmptyRow(b.custom_row)) {
          return 0;
        }

        const companyA = a?.name?.startsWith(' ')
          ? a?.name?.substring(1)
          : a?.name;
        const companyB = b?.name?.startsWith(' ')
          ? b?.name?.substring(1)
          : b?.name;

        return companyA.localeCompare(companyB);
      },
      render: (company: string, record) => {
        if (getEmptyRow(record.custom_row)) return null;
        return <Td>{company}</Td>;
      },
      className: 'hide-on-mobile catalog-table-cell-company',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-price">
          Price
        </Th>
      ),
      key: COLUMN_KEY.PRICE,
      dataIndex: 'last',
      sorter: (a, b) => {
        if (getIgnoreSorting(a, b)) {
          return 0;
        }

        if (getEmptyRow(a.custom_row) || getEmptyRow(b.custom_row)) {
          return 0;
        }

        return cellNumbersorter({
          prevValue: a,
          nextValue: b,
          defaultValueKey: DEFAULT_KEY.LAST,
          liveValueKey: LIVE_VALUE_KEY.LAST,
        });
      },
      render: (price: string, record) => {
        if (getEmptyRow(record.custom_row)) return null;

        let lastPrice = price;
        const livePrice = liveData[record.symbol]?.lastprice;
        if (livePrice) {
          lastPrice = livePrice;
        }

        return <Td align="right"> {numeral(lastPrice).format('0,0')}</Td>;
      },
      className: 'catalog-table-cell-price',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-change">
          Change %
        </Th>
      ),
      key: COLUMN_KEY.CHANGE,
      sorter: (a, b) => {
        if (getIgnoreSorting(a, b)) {
          return 0;
        }

        if (getEmptyRow(a.custom_row) || getEmptyRow(b.custom_row)) {
          return 0;
        }

        return cellNumbersorter({
          prevValue: a,
          nextValue: b,
          defaultValueKey: DEFAULT_KEY.PERCENT,
          liveValueKey: LIVE_VALUE_KEY.PERCENT,
        });
      },
      render: (_, row) => {
        if (getEmptyRow(row.custom_row)) return null;

        let textColor = color.midgray;
        const livePriceChange = liveData[row.symbol]?.change;
        const livePercentChange = liveData[row.symbol]?.percentage_change;

        const showLivePrice =
          typeof livePriceChange !== 'undefined' &&
          typeof livePercentChange !== 'undefined';

        const priceChange = showLivePrice
          ? livePriceChange
          : parseInt(row.change, 10) || 0;

        if (priceChange > 0) {
          textColor = color.primary;
        }
        if (priceChange < 0) {
          textColor = color.sunsetOrange;
        }

        let showedResult = `${priceChange} (${
          priceChange > 0 ? '+' : ''
        }${numeral(row.percent).format('0.00')}%)`;

        if (livePriceChange && livePercentChange) {
          showedResult = `${parseFloat(livePriceChange.toFixed(2))} (${
            livePriceChange > 0 ? '+' : ''
          }${numeral(livePercentChange).format('0.00')}%)`;
        }

        if (row?.change === 'NA') {
          showedResult = `${0} (${numeral(row.percent).format('0.00')}%)`;
        }

        return (
          <Td align="right" color={textColor}>
            {showedResult}
          </Td>
        );
      },
      className: 'catalog-table-cell-change',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-marketcap">
          Market Cap
        </Th>
      ),
      key: COLUMN_KEY.MARKET_CAP,
      dataIndex: 'marketcap',
      shouldCellUpdate: () => false,
      sorter: (a, b) => {
        if (getIgnoreSorting(a, b)) {
          return 0;
        }

        if (getEmptyRow(a.custom_row) || getEmptyRow(b.custom_row)) {
          return 0;
        }

        return a.marketcap - b.marketcap;
      },
      render: (cap: string, record) => {
        if (getEmptyRow(record.custom_row)) return null;

        const defaultValue =
          cap === 'NA' || cap === ''
            ? 'NA'
            : number.numberFormatter({ num: cap, fixed: 0, formatDot: false });
        return <Td align="right">{defaultValue}</Td>;
      },
      className: 'hide-on-mobile catalog-table-cell-marketcap',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-liquidity">
          Liquidity (Rp)
        </Th>
      ),
      key: COLUMN_KEY.LIQUIDITY,
      dataIndex: 'valuema20',
      shouldCellUpdate: () => false,
      sorter: (a, b) => {
        if (getIgnoreSorting(a, b)) {
          return 0;
        }

        if (getEmptyRow(a.custom_row) || getEmptyRow(b.custom_row)) {
          return 0;
        }
        return a.valuema20 - b.valuema20;
      },
      render: (liquidity: string, record) => {
        if (getEmptyRow(record.custom_row)) return null;

        const defaultValue =
          liquidity === 'NA' || liquidity === ''
            ? 'NA'
            : number.numberFormatter({
                num: parseInt(liquidity, 10),
                fixed: 0,
                withThousands: true,
              });

        return <Td align="right">{defaultValue}</Td>;
      },
      className: 'hide-on-mobile catalog-table-cell-liquidity',
    },
  ];

  const columnValueFR = [
    {
      title: (
        <Th align="right" id="catalog-table-column-yield">
          Yield
        </Th>
      ),
      key: COLUMN_KEY.YIELD,
      dataIndex: 'extra_attributes.fr_attributes.yield',
      sorter: (a, b) => {
        const yieldA = parseFloat(a.extra_attributes.fr_attributes?.yield);
        const yieldB = parseFloat(b.extra_attributes.fr_attributes?.yield);
        return yieldA - yieldB;
      },
      render: (_, record) => (
        <Td align="right">
          {numeral(record.extra_attributes.fr_attributes?.yield).format('0.00')}
          %
        </Td>
      ),
      className: 'catalog-table-cell-yield',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-bid-price">
          Harga Beli
        </Th>
      ),
      key: COLUMN_KEY.BID_PRICE,
      sorter: (a, b) => {
        const bidPriceA = parseFloat(
          a.extra_attributes.fr_attributes?.bid_price,
        );
        const bidPriceB = parseFloat(
          b.extra_attributes.fr_attributes?.bid_price,
        );
        return bidPriceA - bidPriceB;
      },
      render: (_, row) => (
        <Td align="right">
          {numeral(row.extra_attributes.fr_attributes?.bid_price).format(
            '0.00',
          )}
          %
        </Td>
      ),
      className: 'catalog-table-cell-bid-price',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-offer-price">
          Harga Jual
        </Th>
      ),
      key: COLUMN_KEY.OFFER_PRICE,
      dataIndex: 'extra_attributes.fr_attributes.offer_price',
      shouldCellUpdate: () => false,
      sorter: (a, b) => {
        const offerPriceA = parseFloat(
          a.extra_attributes.fr_attributes?.offer_price,
        );
        const offerPriceB = parseFloat(
          b.extra_attributes.fr_attributes?.offer_price,
        );
        return offerPriceA - offerPriceB;
      },
      render: (_, record) => (
        <Td align="right">
          {numeral(record.extra_attributes.fr_attributes?.offer_price).format(
            '0.00',
          )}
          %
        </Td>
      ),
      className: 'hide-on-mobile catalog-table-cell-offer-price',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-perfromance">
          Performa 1Y
        </Th>
      ),
      key: COLUMN_KEY.PERFORMANCE,
      dataIndex: 'extra_attributes.fr_attributes.performance',
      shouldCellUpdate: () => false,
      sorter: (a, b) => {
        const percentageA = parseFloat(
          a.extra_attributes.fr_attributes?.performance,
        );
        const percentageB = parseFloat(
          b.extra_attributes.fr_attributes?.performance,
        );

        return percentageA - percentageB;
      },
      render: (_, record) => {
        let textColor = color.midgray;
        let prefix = '';
        const numberPercentage = parseFloat(
          record.extra_attributes.fr_attributes?.performance,
        );

        if (numberPercentage > 0) {
          prefix = '+';
          textColor = color.primary;
        }
        if (numberPercentage < 0) {
          textColor = color.sunsetOrange;
        }
        return (
          <Td align="right" color={textColor}>
            {prefix}
            {numeral(numberPercentage).format('0.00')}%
          </Td>
        );
      },
      className: 'hide-on-mobile catalog-table-cell-performance',
    },
    {
      title: (
        <Th align="right" id="catalog-table-column-due-date">
          Jatuh Tempo
        </Th>
      ),
      key: COLUMN_KEY.DUE_DATE,
      dataIndex: 'extra_attributes.fr_attributes.due_date',
      shouldCellUpdate: () => false,
      sorter: (a, b) => {
        const percentageA = parseFloat(
          a.extra_attributes.fr_attributes?.due_date,
        );
        const percentageB = parseFloat(
          b.extra_attributes.fr_attributes?.due_date,
        );

        return percentageA - percentageB;
      },
      render: (_, record) => (
        <Td align="right">
          {moment(record.extra_attributes.fr_attributes?.due_date).format(
            'DD MMM YYYY',
          )}
        </Td>
      ),
      className: 'hide-on-mobile catalog-table-cell-due-date',
    },
  ];

  const restColumn = isFRCatalog ? columnValueFR : columnValueNonFR;

  const columns: ColumnsType<any> = [
    {
      title: <Th id="catalog-table-column-symbol">Symbol</Th>,
      dataIndex: 'symbol',
      key: COLUMN_KEY.SYMBOL,
      defaultSortOrder: hideDefaultSortOrder ? undefined : 'ascend',
      sorter: {
        compare: (a, b) => {
          if (getIgnoreSorting(a, b)) {
            return 0;
          }

          if (getEmptyRow(a.custom_row) || getEmptyRow(b.custom_row)) {
            return 0;
          }

          return a.symbol?.localeCompare(b.symbol);
        },
      },
      render: (symbol: string, record) => {
        if (getEmptyRow(record.custom_row)) {
          return (
            // @ts-expect-error
            <Td bold style={{ color: '#858585' }}>
              <span>{record.custom_label}</span>
            </Td>
          );
        }

        return (
          // @ts-expect-error
          <Td bold style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Link href={`/symbol/${symbol}`}>
              <a>{symbol}</a>
            </Link>
            <Badge
              symbol={symbol}
              notationList={record.notations}
              subsectorAlias={subsectorAlias}
              name={record.name}
            />

            <If condition={record.grouping_by?.label === 'Today'}>
              <NewBadge>NEW</NewBadge>
            </If>
          </Td>
        );
      },
      fixed: 'left',
      className: 'catalog-table-cell-symbol',
      sortDirections: ['ascend', 'descend', undefined],
    },
    ...restColumn,
  ];

  if (showMultiplier) {
    columns.splice(2, 0, {
      title: <Th id="catalog-table-column-company">Multiplier</Th>,
      key: COLUMN_KEY.MULTIPLIER,
      dataIndex: 'day_trade_info',
      sorter: (a, b) => {
        const multiplierA = a?.day_trade_info?.multiplier || 0;
        const multiplierB = b?.day_trade_info?.multiplier || 0;
        return multiplierA - multiplierB;
      },
      shouldCellUpdate: () => false,
      render: (props) => {
        const { multiplier, is_show_multiplier } = props || {};
        return (
          <DayTradeMultiplier>
            <If condition={is_show_multiplier}>
              <MultiplierBadge>
                <Icon component={LightningFilledSvg} />
                {multiplier}x
              </MultiplierBadge>
            </If>
          </DayTradeMultiplier>
        );
      },
      className: 'catalog-table-cell-multiplier',
    });
  }

  if (isTradingLimitSubsector) {
    columns.splice(2, 0, {
      title: (
        <Th id="catalog-table-column-haircut" style={{ paddingLeft: 0 }}>
          Haircut
        </Th>
      ),
      dataIndex: 'trading_limit_info.haircut_percentage',
      key: COLUMN_KEY.HAIRCUT,
      shouldCellUpdate: () => false,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        const percentageA =
          parseFloat(a.trading_limit_info?.haircut_percentage) || 0;
        const percentageB =
          parseFloat(b.trading_limit_info?.haircut_percentage) || 0;

        return percentageA - percentageB;
      },
      render: (value, record) => (
        <HaircutCell>
          <HaircutBadge>
            {record.trading_limit_info?.haircut_percentage}
          </HaircutBadge>
        </HaircutCell>
      ),
    });
  }

  if (SPECIAL_BOARD_ALIAS.includes(subsectorAlias?.toUpperCase())) {
    columns.splice(4, 2);
  }

  const insertDividerRow = (data = []) => {
    if (isFRCatalog) return data;

    const dataWithDividerRow = [...data];
    let hasTodaySection = false;
    let lastItemType = '';
    let effectiveDate = '';

    data.forEach((item, index) => {
      const currentLabel = item.grouping_by?.label;

      if (lastItemType === currentLabel) return; // Skip if same as previous

      if (currentLabel === 'Today') {
        hasTodaySection = true;
        effectiveDate = item.effective_date
          ? moment(item.effective_date).format('D MMM YY')
          : moment().format('D MMM YY');
      } else if (hasTodaySection) {
        dataWithDividerRow.splice(index, 0, {
          custom_row: 'row_common',
          custom_label: tableTitle,
        });
      }

      lastItemType = currentLabel;
    });

    if (hasTodaySection) {
      dataWithDividerRow.unshift({
        custom_row: 'row_today',
        custom_label: `${
          tableTitle === 'FCA' ? 'New ' : ''
        }${tableTitle} ${effectiveDate}`,
      });
    }

    return dataWithDividerRow;
  };

  return (
    <Table
      id="catalog-table"
      dataSource={insertDividerRow(companyList)}
      columns={columns}
      headerBorder
      onChange={handleChange}
      showSorterTooltip={false}
      isLoading={isLoading}
      rowKey="symbol"
      sortDirections={['ascend', 'descend', 'ascend']}
      rowBorder
      rowClassName={(record: { custom_row: string }) =>
        record?.custom_row
          ? 'catalog-table-body-row no-bottom-border'
          : 'catalog-table-body-row'
      }
    />
  );
}

export default memo(TableCatalog);
